/**
 * HeroCard — Large answer card with title, key stats, optional image
 *
 * The "lead answer" block — styled to match the cyan/dark Jarvis aesthetic.
 */
import React from 'react';
import type { HeroCardData } from '../types';
import { Zap } from '../../shared';

interface HeroCardProps {
  data: HeroCardData;
}

export const HeroCard: React.FC<HeroCardProps> = ({ data }) => {
  const { title, subtitle, summary, image, stats, badge } = data;

  return (
    <div className="relative overflow-hidden rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/30 shadow-[0_0_30px_rgba(34,211,238,0.15)] transition-all duration-300 hover:border-cyan-400/50 hover:shadow-[0_0_40px_rgba(34,211,238,0.25)]">
      {/* Optional hero image */}
      {image && (
        <div className="w-full h-48 md:h-56 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover opacity-80"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className={`p-5 md:p-6 ${image ? 'relative -mt-16 z-10' : ''}`}>
        {/* Badge */}
        {badge && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 text-[10px] font-bold uppercase tracking-wider rounded-full bg-cyan-400/15 text-cyan-300 border border-cyan-400/30">
            <Zap className="w-3 h-3" />
            {badge}
          </span>
        )}

        {/* Title */}
        <h2 className="text-xl md:text-2xl font-bold text-cyan-300 mb-1 drop-shadow-[0_0_5px_rgba(34,211,238,0.3)]">
          {title}
        </h2>

        {/* Subtitle */}
        {subtitle && (
          <p className="text-sm text-cyan-400/60 mb-3 font-medium">{subtitle}</p>
        )}

        {/* Summary */}
        {summary && (
          <p className="text-sm md:text-base text-cyan-300/80 leading-relaxed mb-4">
            {summary}
          </p>
        )}

        {/* Key stats row */}
        {stats && stats.length > 0 && (
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-cyan-400/15">
            {stats.map((stat, i) => (
              <div key={i} className="flex flex-col">
                <span className="text-[10px] text-cyan-400/50 uppercase tracking-wider font-bold">
                  {stat.label}
                </span>
                <span className="text-lg font-bold text-cyan-400 drop-shadow-[0_0_3px_rgba(34,211,238,0.5)]">
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroCard;
